
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Users, Mail, UserPlus, Trash2 } from 'lucide-react';
import { workspaceShareService, WorkspaceShare } from '@/services/supabase';
import { useToast } from '@/hooks/use-toast';

interface SharingPanelProps {
  spaceId: string;
  spaceName: string;
  isOpen: boolean;
  onClose: () => void;
  isOwner: boolean;
}

export const SharingPanel: React.FC<SharingPanelProps> = ({
  spaceId,
  spaceName,
  isOpen,
  onClose,
  isOwner
}) => {
  const [shares, setShares] = useState<Array<WorkspaceShare & { user_email: string }>>([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPermission, setNewUserPermission] = useState<'view' | 'edit'>('view');
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [updatingPermissions, setUpdatingPermissions] = useState<string | null>(null);
  const { toast } = useToast();

  // Load existing shares
  const loadShares = async () => {
    try {
      setLoading(true);
      const sharesData = await workspaceShareService.getByNoteSpaceId(spaceId);
      console.log('Loaded shares:', sharesData);
      setShares(sharesData);
    } catch (error) {
      console.error('Error loading shares:', error);
      toast({
        title: "Error loading collaborators",
        description: "Failed to load the list of collaborators.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && spaceId) {
      loadShares();
    }
  }, [isOpen, spaceId]);

  // Invite new user
  const handleInvite = async () => {
    if (!newUserEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter an email address to invite.",
        variant: "destructive",
      });
      return;
    }

    try {
      setInviteLoading(true);
      await workspaceShareService.create(spaceId, newUserEmail.trim(), newUserPermission);
      
      toast({
        title: "Invitation sent",
        description: `Successfully shared workspace with ${newUserEmail}`,
      });
      
      setNewUserEmail('');
      setNewUserPermission('view');
      await loadShares();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      toast({
        title: "Failed to invite user",
        description: error.message || "Please check the email address and try again.",
        variant: "destructive",
      });
    } finally {
      setInviteLoading(false);
    }
  };

  // Remove collaborator
  const handleRemoveCollaborator = async (shareId: string, userEmail: string) => {
    try {
      await workspaceShareService.delete(shareId);
      
      toast({
        title: "Collaborator removed",
        description: `Removed ${userEmail} from the workspace.`,
      });
      
      await loadShares();
    } catch (error) {
      console.error('Error removing collaborator:', error);
      toast({
        title: "Failed to remove collaborator",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Update permission with detailed logging and no optimistic updates
  const handleUpdatePermission = async (shareId: string, newPermission: 'view' | 'edit', userEmail: string) => {
    console.log('=== PERMISSION UPDATE START ===');
    console.log('Share ID:', shareId);
    console.log('New Permission:', newPermission);
    console.log('User Email:', userEmail);
    console.log('Current shares before update:', shares.map(s => ({ id: s.id, email: s.user_email, permission: s.permission_level })));
    
    try {
      setUpdatingPermissions(shareId);
      
      // Call the database update function directly and log the result
      console.log('Calling workspaceShareService.updatePermission...');
      const result = await workspaceShareService.updatePermission(shareId, newPermission);
      console.log('Database update result:', result);
      
      // Reload data from database to see actual state
      console.log('Reloading shares from database...');
      const freshShares = await workspaceShareService.getByNoteSpaceId(spaceId);
      console.log('Fresh shares from database:', freshShares.map(s => ({ id: s.id, email: s.user_email, permission: s.permission_level })));
      
      setShares(freshShares);
      
      // Check if the update actually worked
      const updatedShare = freshShares.find(s => s.id === shareId);
      if (updatedShare && updatedShare.permission_level === newPermission) {
        console.log('✅ Permission update SUCCEEDED in database');
        toast({
          title: "Permissions updated",
          description: `Updated ${userEmail}'s permissions to ${newPermission}.`,
        });
      } else {
        console.log('❌ Permission update FAILED - database shows:', updatedShare?.permission_level, 'but expected:', newPermission);
        toast({
          title: "Permission update failed",
          description: "The database update did not take effect. Please try again.",
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error('❌ Error during permission update:', error);
      
      // Reload shares to show current database state
      await loadShares();
      
      toast({
        title: "Failed to update permissions",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setUpdatingPermissions(null);
      console.log('=== PERMISSION UPDATE END ===');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Share Workspace</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Sharing: <span className="font-medium">{spaceName}</span>
          </p>
        </div>

        {isOwner && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4 text-gray-500" />
              <Input
                placeholder="Enter email address"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Select value={newUserPermission} onValueChange={(value: 'view' | 'edit') => setNewUserPermission(value)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">Can view</SelectItem>
                  <SelectItem value="edit">Can edit</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                onClick={handleInvite} 
                disabled={inviteLoading}
                className="shrink-0"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Invite
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            Collaborators ({shares.length})
          </h4>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : shares.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              No collaborators yet
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {shares.map((share) => (
                <div key={share.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">
                        {share.user_email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm truncate">{share.user_email}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 shrink-0">
                    {isOwner ? (
                      <>
                        <Select 
                          value={share.permission_level} 
                          onValueChange={(value: 'view' | 'edit') => handleUpdatePermission(share.id, value, share.user_email)}
                          disabled={updatingPermissions === share.id}
                        >
                          <SelectTrigger className="w-20 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="view">View</SelectItem>
                            <SelectItem value="edit">Edit</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCollaborator(share.id, share.user_email)}
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {share.permission_level}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t">
          <Button onClick={onClose} className="w-full">
            Done
          </Button>
        </div>
      </Card>
    </div>
  );
};
